import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "../apiclient/apiclient";
import { format } from "date-fns";
import ThemedTable from "../components/ThemedTable";
import Pagination from "../components/Pagination";
import Loader from "../components/Loader";

const InventoryLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  // Filter states
  const [search, setSearch] = useState("");
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(150);

  // Load all warehouses for the filter dropdown
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await apiClient.get("/locations");
        const list = res.data?.locations || res.data || [];
        setWarehouseOptions(list);
      } catch (err) {
        console.error("Failed to load warehouses", err);
      }
    };
    fetchWarehouses();
  }, []);

  const loadLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search,
        warehouse: filterWarehouse,
        startDate,
        endDate
      };
      const res = await apiClient.get("/stock-movements", { params });
      setLogs(res.data?.data || []);
      setCurrentPage(res.data?.page || 1);
      setTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      console.error("Failed to load stock movements", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterWarehouse, startDate, endDate, limit]);

  // Load logs on filter change
  useEffect(() => {
    loadLogs(1);
  }, [loadLogs]);

  const groupedLogs = {};
  logs.forEach((log) => {
    // Group by product, batch, and the exact second of the action (up to 19 characters)
    if (log.createdAt) {
      const key = `${log.product?._id}_${log.newBatchId || "N/A"}_${log.createdAt.substring(0, 19)}`;
      if (!groupedLogs[key]) {
        groupedLogs[key] = {
          key,
          product: log.product,
          batchId: log.newBatchId,
          oldBatchId: log.oldBatchId,
          expiryDate: log.newExpiryDate,
          oldExpiryDate: log.oldExpiryDate,
          date: log.createdAt,
          updatedBy: log.updatedBy?.name || "System",
          updatedRole: log.updatedBy?.role || "",
          type: log.type || "restock",
          description: log.description || "",
          runningBalance: log.runningBalance || 0,
          warehouses: [],
        };
      }
      const wName = log.warehouse?.name || "Unknown";
      const existingW = groupedLogs[key].warehouses.find(w => w.name === wName);
      if (existingW) {
        existingW.quantity += log.addedQuantity;
      } else {
        groupedLogs[key].warehouses.push({
          name: wName,
          quantity: log.addedQuantity,
        });
      }
    }
  });

  const groupedArray = Object.values(groupedLogs).sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* HEADER */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
        <h1 className="text-xl font-bold text-slate-800">
          Inventory Stock Restock Logs
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Track all product stock additions and movements
        </p>
      </div>

      {/* FILTERS */}
      <div className="border-b bg-[#EFF6FF] p-3 border-slate-200">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative w-full lg:flex-1 lg:min-w-[200px]">
            <input
              type="text"
              placeholder="Search product name..."
              className="px-3 py-2 w-full text-sm bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
            <select
              className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full sm:flex-1 lg:w-40 xl:w-48"
              value={filterWarehouse}
              onChange={(e) => setFilterWarehouse(e.target.value)}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((w, idx) => (
                <option key={idx} value={w.name}>{w.name}</option>
              ))}
            </select>

            <div className="flex gap-2 w-full sm:flex-1 lg:w-auto items-center">
              <input
                type="date"
                className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span className="text-slate-500 text-xs font-semibold">TO</span>
              <input
                type="date"
                className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition w-full"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            
            {(search || filterWarehouse || startDate || endDate) && (
              <button
                onClick={() => {
                  setSearch("");
                  setFilterWarehouse("");
                  setStartDate("");
                  setEndDate("");
                }}
                className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 bg-white p-0">
        {groupedArray.length === 0 && !loading ? (
          <p className="text-gray-500 text-sm text-center py-10">No restock history found.</p>
        ) : (
          <div className="rounded-none border-y border-slate-200">
            <ThemedTable>
              <thead>
                <tr>
                  <th className="w-[18%]">Date</th>
                  <th className="w-[20%]">Product</th>
                  <th className="w-[30%]">Added to Warehouses</th>
                  <th className="w-[10%]">Total Stock</th>
                  <th className="w-[12%]">Batch & Expiry</th>
                  <th className="w-[10%]">Updated By</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center">
                      <Loader size={32} />
                    </td>
                  </tr>
                ) : (
                  groupedArray.map((group, idx) => {
                  const hasPositive = group.warehouses.some((w) => w.quantity > 0);
                  const hasNegative = group.warehouses.some((w) => w.quantity < 0);

                  let actionType = "Restock";
                  let actionColor = "bg-emerald-50 text-emerald-700 border-emerald-100";

                  if (group.type === "return") {
                    actionType = "Return";
                    actionColor = "bg-blue-50 text-[#1e40af] border-blue-100";
                  } else if (group.type === "dispose") {
                    actionType = "Discarded";
                    actionColor = "bg-orange-50 text-orange-700 border-orange-100";
                  } else if (hasPositive && hasNegative) {
                    actionType = "Transfer";
                    actionColor = "bg-blue-50 text-blue-700 border-blue-100";
                  } else if (hasNegative) {
                    actionType = "Deduction";
                    actionColor = "bg-rose-50 text-rose-700 border-rose-100";
                  }

                  let formattedExpiry = "";
                  if (group.expiryDate) {
                    try {
                      const d = new Date(group.expiryDate);
                      if (!isNaN(d.getTime())) {
                        formattedExpiry = format(d, "dd MMM yyyy");
                      }
                    } catch (e) {}
                  }

                  return (
                    <tr key={idx}>
                      <td className="font-medium text-slate-600">
                        <span className="block text-slate-800">{format(new Date(group.date), "dd MMM yyyy")}</span>
                        <span className="block text-[10px] text-slate-400 mt-0.5">{format(new Date(group.date), "hh:mm a")}</span>
                      </td>
                      <td className="text-slate-800">
                        <span className="font-bold block">{group.product?.name || "Unknown"}</span>
                        {group.description && (
                          <span className="text-[10px] text-slate-500 block mt-0.5 leading-tight">{group.description}</span>
                        )}
                        <span className={`inline-block text-[9px] font-bold px-1.5 py-0.2 mt-1.5 rounded border ${actionColor} uppercase tracking-wider`}>
                          {actionType}
                        </span>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-2">
                          {group.warehouses.map((w, wIdx) => (
                            <div key={wIdx} className={`flex items-center gap-1.5 border rounded px-2.5 py-0.5 text-xs ${w.quantity >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                              <span className="font-semibold text-slate-700">{w.name}</span>
                              <span className="text-slate-300">|</span>
                              <span className={`font-bold ${w.quantity >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {w.quantity > 0 ? `+${w.quantity}` : w.quantity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="font-bold text-slate-800 text-sm">
                        {group.runningBalance}
                      </td>
                      <td>
                        <div className="text-xs text-slate-800 font-medium flex flex-col gap-0.5">
                          {((group.batchId && group.batchId !== "N/A") || formattedExpiry) ? (
                            <>
                              {group.batchId && group.batchId !== "N/A" && (
                                <span className="flex items-center gap-1">
                                  Batch: <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-500 border border-slate-200 shadow-sm">{group.batchId}</span>
                                </span>
                              )}
                              {formattedExpiry && (
                                <span className="text-slate-500 font-semibold">Exp: {formattedExpiry}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-400 italic font-normal">-</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-xs font-semibold text-slate-700 flex flex-col">
                          <span>{group.updatedBy}</span>
                          <span className="text-[9px] text-slate-400 uppercase font-bold mt-0.5">{group.updatedRole}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
                )}
              </tbody>
            </ThemedTable>
          </div>
        )}
        
        {/* PAGINATION */}
        <div className="border-t border-slate-200 py-4 flex justify-center bg-slate-50">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => loadLogs(page)}
          />
        </div>
      </div>
    </div>
  );
};

export default InventoryLogs;
